import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeLoanCreateDialog } from "@/components/hr/EmployeeLoanCreateDialog";
import { EmployeeSelect } from "@/components/hr/EmployeeSelect";
import { emptyLoanPaymentDraft, LoanPaymentMethodFields } from "@/components/hr/LoanPaymentMethodFields";
import { SalaryPeriodFilterField } from "@/components/hr/SalaryPeriodMonthPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  deleteEmployeeLoan,
  listAllEmployeeLoans,
  listLoanInstallments,
  loansApiErrorMessage,
  recordLoanRepayment,
  skipLoanInstallment,
  validateLoanPayment,
  type EmployeeLoanInstallmentRecord,
  type EmployeeLoanRecord,
  type LoanStatus,
} from "@/lib/hr/loansStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import type { ExpensePaymentMethod } from "@/lib/expenses/expensesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { addMonths, format, isValid, parseISO } from "date-fns";
import {
  Banknote,
  Calculator,
  CalendarIcon,
  Edit,
  Eye,
  Filter,
  HandCoins,
  Info,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  User,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const LOANS_FETCH_CAP = 500;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

type InstallmentListStatus = "paid" | "pending" | "skip";

type InstallmentListRow = {
  id: string;
  salaryId: string | null;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  amount: string;
  deductedOn: string | null;
  status: InstallmentListStatus;
  note: string | null;
  projected: boolean;
  isOutsideRepayment: boolean;
};

function employeePhoto(employees: EmployeeRecord[], employeeId: string): string | null {
  return employees.find((x) => x.id === employeeId)?.photoDataUrl?.trim() || null;
}

function parseLoanMoney(amount: string | number | null | undefined): number | null {
  if (amount == null || amount === "") return null;
  const n = typeof amount === "number" ? amount : Number(String(amount).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function employeeInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "E";
}

function LoanEmployeeAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <div className="h-9 w-9 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-bold text-primary">{employeeInitial(name)}</span>
      )}
    </div>
  );
}

function sortInstallmentsChronological(rows: InstallmentListRow[]): InstallmentListRow[] {
  return rows.slice().sort((a, b) => {
    if (a.periodYear !== b.periodYear) return a.periodYear - b.periodYear;
    if (a.periodMonth !== b.periodMonth) return a.periodMonth - b.periodMonth;
    if (a.isOutsideRepayment !== b.isOutsideRepayment) return a.isOutsideRepayment ? -1 : 1;
    const aDate = a.deductedOn || "";
    const bDate = b.deductedOn || "";
    if (aDate !== bDate) return aDate.localeCompare(bDate);
    return a.id.localeCompare(b.id);
  });
}

function formatReceivedOnLabel(dateStr: string | null): string | null {
  if (!dateStr?.trim()) return null;
  const parsed = parseISO(dateStr.length <= 10 ? `${dateStr}T00:00:00` : dateStr);
  if (!isValid(parsed)) return null;
  return `Rec: ${format(parsed, "d MMM yyyy")}`;
}

function LoanInstallmentsPanel({
  rows,
}: {
  rows: InstallmentListRow[];
}) {
  const ordered = sortInstallmentsChronological(rows);
  const paidCount = ordered.filter((x) => x.status === "paid").length;
  const pendingCount = ordered.filter((x) => x.status === "pending").length;
  const skipCount = ordered.filter((x) => x.status === "skip").length;
  const scheduleTotal = ordered.reduce((sum, row) => sum + (parseLoanMoney(row.amount) ?? 0), 0);
  const statusBits = [
    `${paidCount} paid`,
    pendingCount ? `${pendingCount} pending` : null,
    skipCount ? `${skipCount} skip` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-1.5">
      {statusBits.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">{statusBits.join(" · ")}</p>
      ) : null}
      <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="text-left font-semibold py-2 px-3">#</th>
              <th className="text-left font-semibold py-2 px-3">Period</th>
              <th className="text-left font-semibold py-2 px-3">Status</th>
              <th className="text-left font-semibold py-2 px-3">Deducted</th>
              <th className="text-left font-semibold py-2 px-3">Note</th>
              <th className="text-right font-semibold py-2 px-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((x, index) => {
              const receivedLabel = x.status === "paid" ? formatReceivedOnLabel(x.deductedOn) : null;
              return (
                <tr key={x.id} className="border-b border-border/40 last:border-0">
                  <td className="py-2 px-3 tabular-nums text-muted-foreground align-top">{index + 1}</td>
                  <td className="py-2 px-3 font-medium text-foreground whitespace-nowrap align-top">{x.periodLabel}</td>
                  <td className="py-2 px-3 align-top">
                    <div className="space-y-0.5">
                      {installmentStatusBadge(x.status)}
                      {receivedLabel ? (
                        <p className="text-[10px] italic text-muted-foreground leading-tight">{receivedLabel}</p>
                      ) : null}
                    </div>
                  </td>
                  <td className="py-2 px-3 tabular-nums text-muted-foreground whitespace-nowrap align-top">
                    {x.deductedOn || "—"}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground max-w-[14rem] align-top">
                    <span className="line-clamp-2" title={x.note ?? undefined}>
                      {x.note?.trim() || "—"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 align-top">
                    {formatCurrency(parseLoanMoney(x.amount))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30 font-semibold">
              <td className="py-2 px-3" colSpan={5}>
                Schedule total
              </td>
              <td className="py-2 px-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                {formatCurrency(scheduleTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function formatShortPeriodLabel(year: number, month: number): string {
  if (year < 1 || month < 1 || month > 12) return "—";
  return format(new Date(year, month - 1, 1), "MMM yyyy");
}

type RepaymentImpactRow = {
  key: string;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  amount: number;
  kind: "paid" | "skip" | "repayment" | "pending";
};

type RepaymentImpact = {
  remainingBefore: number;
  remainingAfter: number;
  repaymentPeriodLabel: string;
  coveredPendingLabels: string[];
  rows: RepaymentImpactRow[];
};

function advanceYearMonth(year: number, month: number): { year: number; month: number } {
  let y = year;
  let m = month + 1;
  if (m > 12) {
    m = 1;
    y += 1;
  }
  return { year: y, month: m };
}

/** Preview how an outside repayment reshapes the pending schedule for a loan. */
function computeRepaymentImpact(
  loan: EmployeeLoanRecord,
  schedule: InstallmentListRow[],
  repayAmount: number,
): RepaymentImpact | null {
  if (!Number.isFinite(repayAmount) || repayAmount <= 0) return null;

  const remainingBefore = Math.max(0, parseLoanMoney(loan.remainingAmount) ?? 0);
  if (repayAmount - remainingBefore > 0.005) return null;

  const installmentAmt = Math.max(0, parseLoanMoney(loan.installmentAmount) ?? 0);
  const ordered = sortInstallmentsChronological(schedule);
  const settled = ordered.filter((r) => r.status === "paid" || r.status === "skip");
  const pending = ordered.filter((r) => r.status === "pending");

  let anchorYear: number;
  let anchorMonth: number;
  if (pending[0]) {
    anchorYear = pending[0].periodYear;
    anchorMonth = pending[0].periodMonth;
  } else {
    const start = loanStartPeriod(loan);
    if (settled.length > 0) {
      const last = settled[settled.length - 1]!;
      const next = advanceYearMonth(last.periodYear, last.periodMonth);
      anchorYear = next.year;
      anchorMonth = next.month;
    } else if (start) {
      anchorYear = start.year;
      anchorMonth = start.month;
    } else {
      const now = new Date();
      anchorYear = now.getFullYear();
      anchorMonth = now.getMonth() + 1;
    }
  }

  const remainingAfter = Math.round((remainingBefore - repayAmount) * 100) / 100;
  const coveredPendingLabels = pending
    .filter((p) => {
      const key = periodKey(p.periodYear, p.periodMonth);
      const anchorKey = periodKey(anchorYear, anchorMonth);
      return key >= anchorKey;
    })
    .map((p) => formatShortPeriodLabel(p.periodYear, p.periodMonth));

  const rows: RepaymentImpactRow[] = [];

  let bal = remainingAfter;
  let cursor = advanceYearMonth(anchorYear, anchorMonth);
  let guard = 0;
  while (bal >= 0.005 && guard < 60) {
    const amt = Math.min(installmentAmt > 0 ? installmentAmt : bal, bal);
    bal = Math.round((bal - amt) * 100) / 100;
    rows.push({
      key: `pending-${cursor.year}-${cursor.month}`,
      periodYear: cursor.year,
      periodMonth: cursor.month,
      periodLabel: formatShortPeriodLabel(cursor.year, cursor.month),
      amount: amt,
      kind: "pending",
    });
    cursor = advanceYearMonth(cursor.year, cursor.month);
    guard += 1;
  }

  rows.sort(
    (a, b) => a.periodYear - b.periodYear || a.periodMonth - b.periodMonth || a.key.localeCompare(b.key),
  );

  return {
    remainingBefore,
    remainingAfter,
    repaymentPeriodLabel: formatShortPeriodLabel(anchorYear, anchorMonth),
    coveredPendingLabels,
    rows,
  };
}

type LoanRepaymentAllocation = {
  loan: EmployeeLoanRecord;
  amount: number;
  impact: RepaymentImpact;
};

/** Apply repayment FIFO to active loans (oldest installment start first). */
function allocateRepaymentAcrossLoans(
  loans: EmployeeLoanRecord[],
  schedules: Record<string, InstallmentListRow[]>,
  repayAmount: number,
): { allocations: LoanRepaymentAllocation[]; totalRemaining: number; applied: number; excess: number } {
  const ordered = [...loans].sort((a, b) => {
    const aStart = loanStartPeriod(a);
    const bStart = loanStartPeriod(b);
    const aKey = periodKey(aStart?.year ?? 9999, aStart?.month ?? 12);
    const bKey = periodKey(bStart?.year ?? 9999, bStart?.month ?? 12);
    if (aKey !== bKey) return aKey.localeCompare(bKey);
    return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
  });

  const totalRemaining = ordered.reduce((sum, l) => sum + (parseLoanMoney(l.remainingAmount) ?? 0), 0);
  const allocations: LoanRepaymentAllocation[] = [];
  let left = Math.round(repayAmount * 100) / 100;

  for (const loan of ordered) {
    if (left < 0.005) break;
    const rem = Math.max(0, parseLoanMoney(loan.remainingAmount) ?? 0);
    if (rem < 0.005) continue;
    const slice = Math.min(rem, left);
    const impact = computeRepaymentImpact(loan, schedules[loan.id] ?? [], slice);
    if (!impact) continue;
    allocations.push({ loan, amount: slice, impact });
    left = Math.round((left - slice) * 100) / 100;
  }

  const applied = Math.round((repayAmount - left) * 100) / 100;
  const excess = Math.max(0, Math.round((repayAmount - totalRemaining) * 100) / 100);

  return { allocations, totalRemaining, applied, excess };
}

function loanOptionLabel(loan: EmployeeLoanRecord): string {
  const start = loanStartPeriod(loan);
  const period = start ? formatPeriodLabel(start.year, start.month) : "Loan";
  const rem = formatCurrency(parseLoanMoney(loan.remainingAmount));
  const total = formatCurrency(parseLoanMoney(loan.loanAmount));
  return `${period} · ${total} · Rem ${rem}`;
}

function periodKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function formatPeriodLabel(year: number, month: number): string {
  const name = MONTH_NAMES[month - 1];
  return name ? `${name} ${year}` : periodKey(year, month);
}

function loanStartPeriod(loan: EmployeeLoanRecord): { year: number; month: number } | null {
  if (!loan.startDate.trim()) return null;
  const start = parseISO(loan.startDate.length <= 10 ? `${loan.startDate}T00:00:00` : loan.startDate);
  if (!isValid(start)) return null;
  return { year: start.getFullYear(), month: start.getMonth() + 1 };
}

type LoanEmployeeGroup = {
  key: string;
  employeeId: string;
  employeeName: string;
  /** Null when grouped by employee only (all periods). */
  periodYear: number | null;
  /** Null when grouped by employee only (all periods). */
  periodMonth: number | null;
  loans: EmployeeLoanRecord[];
  totalLoanAmount: number;
  totalRemaining: number;
  activeCount: number;
  completedCount: number;
  updatedAt: string;
  createdAt: string;
};

function buildLoanGroup(key: string, loans: EmployeeLoanRecord[], includePeriod: boolean): LoanEmployeeGroup {
  const sorted = [...loans].sort((a, b) => {
    const aPeriod = loanStartPeriod(a);
    const bPeriod = loanStartPeriod(b);
    const periodCmp = periodKey(bPeriod?.year ?? 0, bPeriod?.month ?? 0).localeCompare(
      periodKey(aPeriod?.year ?? 0, aPeriod?.month ?? 0),
    );
    if (periodCmp !== 0) return periodCmp;
    return b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id);
  });
  const head = sorted[0];
  const period = includePeriod ? loanStartPeriod(head!) : null;
  return {
    key,
    employeeId: head?.employeeId ?? "",
    employeeName: head?.employeeName || "",
    periodYear: period?.year ?? null,
    periodMonth: period?.month ?? null,
    loans: sorted,
    totalLoanAmount: sorted.reduce((sum, row) => sum + (parseLoanMoney(row.loanAmount) ?? 0), 0),
    totalRemaining: sorted.reduce((sum, row) => sum + (parseLoanMoney(row.remainingAmount) ?? 0), 0),
    activeCount: sorted.filter((row) => row.status === "active").length,
    completedCount: sorted.filter((row) => row.status === "completed").length,
    updatedAt: sorted.reduce((max, row) => (row.updatedAt > max ? row.updatedAt : max), head?.updatedAt ?? ""),
    createdAt: sorted.reduce((min, row) => (row.createdAt < min ? row.createdAt : min), head?.createdAt ?? ""),
  };
}

function groupLoansByEmployee(loans: EmployeeLoanRecord[]): LoanEmployeeGroup[] {
  const map = new Map<string, EmployeeLoanRecord[]>();
  for (const loan of loans) {
    const key = loan.employeeId || loan.id;
    const bucket = map.get(key) ?? [];
    bucket.push(loan);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([key, groupLoans]) => buildLoanGroup(key, groupLoans, false))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

function groupLoansByEmployeeMonth(loans: EmployeeLoanRecord[]): LoanEmployeeGroup[] {
  const map = new Map<string, EmployeeLoanRecord[]>();
  for (const loan of loans) {
    const period = loanStartPeriod(loan);
    const periodPart = period ? periodKey(period.year, period.month) : "unknown";
    const key = `${loan.employeeId || loan.id}|${periodPart}`;
    const bucket = map.get(key) ?? [];
    bucket.push(loan);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([key, groupLoans]) => buildLoanGroup(key, groupLoans, true))
    .sort((a, b) => {
      const periodCmp = periodKey(b.periodYear ?? 0, b.periodMonth ?? 0).localeCompare(
        periodKey(a.periodYear ?? 0, a.periodMonth ?? 0),
      );
      if (periodCmp !== 0) return periodCmp;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
}

function loansByStartMonth(loans: EmployeeLoanRecord[]): Array<{
  key: string;
  label: string;
  loans: EmployeeLoanRecord[];
  totalLoanAmount: number;
  totalRemaining: number;
}> {
  const map = new Map<string, EmployeeLoanRecord[]>();
  for (const loan of loans) {
    const period = loanStartPeriod(loan);
    const key = period ? periodKey(period.year, period.month) : "unknown";
    const bucket = map.get(key) ?? [];
    bucket.push(loan);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([key, monthLoans]) => {
      const period = loanStartPeriod(monthLoans[0]!);
      return {
        key,
        label: period ? formatPeriodLabel(period.year, period.month) : "Unknown period",
        loans: monthLoans,
        totalLoanAmount: monthLoans.reduce((sum, row) => sum + (parseLoanMoney(row.loanAmount) ?? 0), 0),
        totalRemaining: monthLoans.reduce((sum, row) => sum + (parseLoanMoney(row.remainingAmount) ?? 0), 0),
      };
    })
    .sort((a, b) => b.key.localeCompare(a.key));
}

function loanGroupPeriodSummary(group: LoanEmployeeGroup): string {
  if (group.periodYear != null && group.periodMonth != null) {
    return formatPeriodLabel(group.periodYear, group.periodMonth);
  }
  const periods = Array.from(
    new Set(
      group.loans
        .map((loan) => loanStartPeriod(loan))
        .filter((p): p is { year: number; month: number } => p != null)
        .map((p) => formatPeriodLabel(p.year, p.month)),
    ),
  );
  if (periods.length === 0) return "All periods";
  if (periods.length === 1) return periods[0]!;
  return `${periods.length} periods`;
}

function LoanStatusPill({ status }: { status: LoanStatus }) {
  return (
    <span
      className={cn(
        "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em]",
        status === "completed"
          ? "bg-muted text-muted-foreground"
          : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      )}
    >
      <span
        className={cn("h-1.5 w-1.5 rounded-full", status === "completed" ? "bg-muted-foreground/70" : "bg-emerald-500")}
        aria-hidden
      />
      {status === "completed" ? "Completed" : "Active"}
    </span>
  );
}

function mapInstallmentStatus(status: EmployeeLoanInstallmentRecord["status"]): InstallmentListStatus {
  if (status === "paid") return "paid";
  if (status === "unpaid") return "skip";
  return "pending";
}

function installmentStatusBadge(status: InstallmentListStatus) {
  if (status === "paid") {
    return (
      <Badge className="border-transparent bg-emerald-600/15 text-emerald-700 hover:bg-emerald-600/20 dark:text-emerald-400">
        Paid
      </Badge>
    );
  }
  if (status === "skip") {
    return (
      <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
        Skip
      </Badge>
    );
  }
  return <Badge variant="secondary">Pending</Badge>;
}

function buildInstallmentSchedule(
  loan: EmployeeLoanRecord,
  rows: EmployeeLoanInstallmentRecord[],
): InstallmentListRow[] {
  const payrollRows = rows.filter((r) => !r.isOutsideRepayment);
  const outsideRows = [...rows.filter((r) => r.isOutsideRepayment)].sort((a, b) => {
    const aDate = a.deductedOn || "";
    const bDate = b.deductedOn || "";
    if (aDate !== bDate) return aDate.localeCompare(bDate);
    return a.id.localeCompare(b.id);
  });

  const byPeriod = new Map<string, EmployeeLoanInstallmentRecord>();
  for (const row of payrollRows) {
    const y = Number(row.periodYear) || 0;
    const m = Number(row.periodMonth) || 0;
    if (y < 1 || m < 1) continue;
    byPeriod.set(periodKey(y, m), row);
  }

  const start = parseISO(loan.startDate.length <= 10 ? `${loan.startDate}T00:00:00` : loan.startDate);
  let year = isValid(start) ? start.getFullYear() : new Date().getFullYear();
  let month = isValid(start) ? start.getMonth() + 1 : new Date().getMonth() + 1;
  const installmentAmt = Math.max(0, parseLoanMoney(loan.installmentAmount) ?? 0);
  const defaultAmount = loan.installmentAmount;
  const loanAmt = Math.max(0, parseLoanMoney(loan.loanAmount) ?? 0);

  // No outside returns: original projected schedule (cap pending by remaining when completed).
  if (outsideRows.length === 0) {
    const remainingCap = Math.max(0, parseLoanMoney(loan.remainingAmount) ?? loanAmt);
    const paidTotal = payrollRows
      .filter((r) => r.status === "paid")
      .reduce((sum, r) => sum + (parseLoanMoney(r.installmentAmount) ?? 0), 0);
    let remaining = Math.max(0, Math.round((loanAmt - paidTotal) * 100) / 100);
    // Prefer server remaining when loan is completed / remaining is authoritative.
    if (loan.status === "completed" || remainingCap < remaining) {
      remaining = remainingCap;
    }

    const total = Math.max(1, Number(loan.totalInstallments) || 1);
    const skipCount = payrollRows.filter((r) => r.status === "unpaid").length;
    const maxMonths = total + skipCount + 36;
    const scheduled: InstallmentListRow[] = [];
    let deductionSlots = 0;

    for (let i = 0; i < maxMonths && deductionSlots < total; i += 1) {
      const key = periodKey(year, month);
      const existing = byPeriod.get(key);
      if (existing) {
        byPeriod.delete(key);
        const status = mapInstallmentStatus(existing.status);
        if (status === "skip") {
          scheduled.push({
            id: existing.id,
            salaryId: existing.salaryId,
            periodYear: year,
            periodMonth: month,
            periodLabel: formatPeriodLabel(year, month),
            amount: "0",
            deductedOn: existing.deductedOn,
            status: "skip",
            note: existing.note,
            projected: false,
            isOutsideRepayment: false,
          });
        } else if (status === "paid") {
          scheduled.push({
            id: existing.id,
            salaryId: existing.salaryId,
            periodYear: year,
            periodMonth: month,
            periodLabel: formatPeriodLabel(year, month),
            amount: existing.installmentAmount || defaultAmount,
            deductedOn: existing.deductedOn,
            status: "paid",
            note: existing.note,
            projected: false,
            isOutsideRepayment: false,
          });
          deductionSlots += 1;
        } else {
          if (remaining < 0.005) break;
          const raw = Math.max(0, parseLoanMoney(existing.installmentAmount) ?? installmentAmt);
          const amt = Math.min(raw > 0 ? raw : remaining, remaining);
          remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
          scheduled.push({
            id: existing.id,
            salaryId: existing.salaryId,
            periodYear: year,
            periodMonth: month,
            periodLabel: formatPeriodLabel(year, month),
            amount: String(amt),
            deductedOn: existing.deductedOn,
            status: "pending",
            note: existing.note,
            projected: false,
            isOutsideRepayment: false,
          });
          deductionSlots += 1;
        }
      } else {
        if (remaining < 0.005) break;
        const amt = Math.min(installmentAmt > 0 ? installmentAmt : remaining, remaining);
        remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
        scheduled.push({
          id: `projected-${key}`,
          salaryId: null,
          periodYear: year,
          periodMonth: month,
          periodLabel: formatPeriodLabel(year, month),
          amount: String(amt),
          deductedOn: null,
          status: "pending",
          note: null,
          projected: true,
          isOutsideRepayment: false,
        });
        deductionSlots += 1;
      }
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }

    const leftovers = Array.from(byPeriod.values()).map((existing) => {
      const y = Number(existing.periodYear) || 0;
      const m = Number(existing.periodMonth) || 0;
      const status = mapInstallmentStatus(existing.status);
      return {
        id: existing.id,
        salaryId: existing.salaryId,
        periodYear: y,
        periodMonth: m,
        periodLabel: formatPeriodLabel(y, m),
        amount: status === "skip" ? "0" : existing.installmentAmount || defaultAmount,
        deductedOn: existing.deductedOn,
        status,
        note: existing.note,
        projected: false,
        isOutsideRepayment: false,
      } satisfies InstallmentListRow;
    });

    return [...scheduled, ...leftovers].sort(
      (a, b) => a.periodYear - b.periodYear || a.periodMonth - b.periodMonth || a.id.localeCompare(b.id),
    );
  }

  // With loan returns: fold them into the schedule as paid actual amounts and
  // rebuild pending installments from remaining balance (last installment adjusted).
  let remaining = loanAmt;
  const outsideQueue = [...outsideRows];
  const scheduled: InstallmentListRow[] = [];
  const maxMonths = Math.max(1, Number(loan.totalInstallments) || 1) + outsideQueue.length + 48;

  const advancePeriod = () => {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  };

  for (let i = 0; i < maxMonths; i += 1) {
    const hasWork = remaining >= 0.005 || outsideQueue.length > 0 || byPeriod.size > 0;
    if (!hasWork) break;

    const key = periodKey(year, month);
    const existing = byPeriod.get(key);

    if (existing) {
      byPeriod.delete(key);
      const status = mapInstallmentStatus(existing.status);

      if (status === "skip") {
        scheduled.push({
          id: existing.id,
          salaryId: existing.salaryId,
          periodYear: year,
          periodMonth: month,
          periodLabel: formatPeriodLabel(year, month),
          amount: "0",
          deductedOn: existing.deductedOn,
          status: "skip",
          note: existing.note,
          projected: false,
          isOutsideRepayment: false,
        });
        advancePeriod();
        continue;
      }

      if (status === "paid") {
        const amt = Math.max(0, parseLoanMoney(existing.installmentAmount) ?? installmentAmt);
        remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
        scheduled.push({
          id: existing.id,
          salaryId: existing.salaryId,
          periodYear: year,
          periodMonth: month,
          periodLabel: formatPeriodLabel(year, month),
          amount: existing.installmentAmount || defaultAmount,
          deductedOn: existing.deductedOn,
          status: "paid",
          note: existing.note,
          projected: false,
          isOutsideRepayment: false,
        });
        advancePeriod();
        continue;
      }

      // Pending payroll — keep period, amount capped to remaining.
      if (remaining < 0.005 && outsideQueue.length === 0) {
        advancePeriod();
        continue;
      }
      const rawAmt = Math.max(0, parseLoanMoney(existing.installmentAmount) ?? installmentAmt);
      const amt = Math.min(rawAmt > 0 ? rawAmt : remaining, remaining);
      remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
      scheduled.push({
        id: existing.id,
        salaryId: existing.salaryId,
        periodYear: year,
        periodMonth: month,
        periodLabel: formatPeriodLabel(year, month),
        amount: String(amt),
        deductedOn: existing.deductedOn,
        status: "pending",
        note: existing.note,
        projected: false,
        isOutsideRepayment: false,
      });
      advancePeriod();
      continue;
    }

    // Free period: place next loan return here with the actual received amount.
    if (outsideQueue.length > 0) {
      const out = outsideQueue.shift()!;
      const amt = Math.max(0, parseLoanMoney(out.installmentAmount) ?? 0);
      remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
      scheduled.push({
        id: out.id,
        salaryId: null,
        periodYear: year,
        periodMonth: month,
        periodLabel: formatPeriodLabel(year, month),
        amount: out.installmentAmount,
        deductedOn: out.deductedOn,
        status: "paid",
        note: out.note?.trim() || "Outside salary repayment",
        projected: false,
        isOutsideRepayment: true,
      });
      advancePeriod();
      continue;
    }

    if (remaining < 0.005) break;

    // Project remaining balance into installment-sized pending rows (last one adjusted).
    const amt = Math.min(installmentAmt > 0 ? installmentAmt : remaining, remaining);
    remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
    scheduled.push({
      id: `projected-${key}`,
      salaryId: null,
      periodYear: year,
      periodMonth: month,
      periodLabel: formatPeriodLabel(year, month),
      amount: String(amt),
      deductedOn: null,
      status: "pending",
      note: null,
      projected: true,
      isOutsideRepayment: false,
    });
    advancePeriod();
  }

  for (const out of outsideQueue) {
    const y = Number(out.periodYear) || year;
    const m = Number(out.periodMonth) || month;
    scheduled.push({
      id: out.id,
      salaryId: null,
      periodYear: y,
      periodMonth: m,
      periodLabel: y && m ? formatPeriodLabel(y, m) : "Outside",
      amount: out.installmentAmount,
      deductedOn: out.deductedOn,
      status: "paid",
      note: out.note?.trim() || "Outside salary repayment",
      projected: false,
      isOutsideRepayment: true,
    });
  }

  return scheduled.sort(
    (a, b) => a.periodYear - b.periodYear || a.periodMonth - b.periodMonth || a.id.localeCompare(b.id),
  );
}

type LoanSortBy = "latest" | "oldest" | "amount_desc" | "amount_asc";

export default function EmployeeLoans() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "employee_loan_can_create");
  const canUpdate = can(user, "employee_loan_can_update");
  const canClose = can(user, "employee_loan_can_close");
  const canViewInstallments = can(user, "employee_loan_can_view_installments");
  const canSkipInstallment = can(user, "employee_loan_can_skip_installment");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const [banks, setBanks] = useState<BankOption[]>([]);

  const [rows, setRows] = useState<EmployeeLoanRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | LoanStatus>("all");
  /** Installment start salary period; null = show all (default listing). */
  const [filterPeriodYear, setFilterPeriodYear] = useState<number | null>(null);
  const [filterPeriodMonth, setFilterPeriodMonth] = useState<number | null>(null);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState<LoanSortBy>("latest");

  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const hasPeriodFilter = filterPeriodYear != null && filterPeriodMonth != null;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeLoanRecord | null>(null);
  const [deletingTarget, setDeletingTarget] = useState<EmployeeLoanRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewTarget, setViewTarget] = useState<LoanEmployeeGroup | null>(null);
  const [viewSchedules, setViewSchedules] = useState<Record<string, InstallmentListRow[]>>({});
  const [viewSchedulesLoading, setViewSchedulesLoading] = useState(false);

  const [skipOpen, setSkipOpen] = useState(false);
  const [skipLoan, setSkipLoan] = useState<EmployeeLoanRecord | null>(null);
  const [skipMonth, setSkipMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [skipNote, setSkipNote] = useState("");
  const [skipping, setSkipping] = useState(false);

  const [repayOpen, setRepayOpen] = useState(false);
  const [repayAmount, setRepayAmount] = useState("");
  const [repayDate, setRepayDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [repayNote, setRepayNote] = useState("");
  const [repayPayment, setRepayPayment] = useState(emptyLoanPaymentDraft);
  const [repaying, setRepaying] = useState(false);
  const [skipPickerOpen, setSkipPickerOpen] = useState(false);
  const [skipPickerYear, setSkipPickerYear] = useState(() => new Date().getFullYear());

  const monthOptions = useMemo(
    () => [
      { v: 1, label: "Jan" },
      { v: 2, label: "Feb" },
      { v: 3, label: "Mar" },
      { v: 4, label: "Apr" },
      { v: 5, label: "May" },
      { v: 6, label: "Jun" },
      { v: 7, label: "Jul" },
      { v: 8, label: "Aug" },
      { v: 9, label: "Sep" },
      { v: 10, label: "Oct" },
      { v: 11, label: "Nov" },
      { v: 12, label: "Dec" },
    ],
    [],
  );

  const skipMonthLabel = useMemo(() => {
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) return "Pick month";
    const [yy, mm] = m.split("-").map((x) => Number(x));
    const label = monthOptions.find((x) => x.v === mm)?.label ?? String(mm);
    return `${label} ${yy}`;
  }, [skipMonth, monthOptions]);

  const repayActiveLoans = useMemo(() => {
    if (!viewTarget) return [];
    return viewTarget.loans
      .filter((l) => l.status === "active" && (parseLoanMoney(l.remainingAmount) ?? 0) > 0.005)
      .slice()
      .sort((a, b) => {
        const aStart = loanStartPeriod(a);
        const bStart = loanStartPeriod(b);
        const aKey = periodKey(aStart?.year ?? 9999, aStart?.month ?? 12);
        const bKey = periodKey(bStart?.year ?? 9999, bStart?.month ?? 12);
        if (aKey !== bKey) return aKey.localeCompare(bKey);
        return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
      });
  }, [viewTarget]);

  const repayTotalRemaining = useMemo(
    () => repayActiveLoans.reduce((sum, l) => sum + (parseLoanMoney(l.remainingAmount) ?? 0), 0),
    [repayActiveLoans],
  );

  const repayAmountNumber = useMemo(() => {
    const n = Number(String(repayAmount).replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }, [repayAmount]);

  const repayPlan = useMemo(() => {
    if (repayAmountNumber <= 0 || repayActiveLoans.length === 0) return null;
    return allocateRepaymentAcrossLoans(repayActiveLoans, viewSchedules, repayAmountNumber);
  }, [repayActiveLoans, viewSchedules, repayAmountNumber]);

  const repayImpact = repayPlan && repayPlan.allocations.length > 0 && repayPlan.excess < 0.005 ? repayPlan : null;

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
    void fetchBanks()
      .then(setBanks)
      .catch(() => setBanks([]));
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllEmployeeLoans({
        page: 1,
        perPage: LOANS_FETCH_CAP,
      });
      setRows(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(loansApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const scopedRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterEmployeeId && r.employeeId !== filterEmployeeId) return false;
      if (hasPeriodFilter) {
        const start = loanStartPeriod(r);
        if (!start) return false;
        if (start.year !== filterPeriodYear || start.month !== filterPeriodMonth) return false;
      }
      return true;
    });
  }, [rows, filterStatus, filterEmployeeId, hasPeriodFilter, filterPeriodYear, filterPeriodMonth]);

  const groups = useMemo(
    () => (hasPeriodFilter ? groupLoansByEmployeeMonth(scopedRows) : groupLoansByEmployee(scopedRows)),
    [scopedRows, hasPeriodFilter],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => {
      const hay = `${group.employeeName} ${group.totalLoanAmount} ${group.totalRemaining} ${loanGroupPeriodSummary(group)} ${group.loans.map((l) => `${l.loanAmount} ${l.installmentAmount} ${l.status}`).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [groups, query]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = periodKey(a.periodYear ?? 0, a.periodMonth ?? 0);
      const bPeriod = periodKey(b.periodYear ?? 0, b.periodMonth ?? 0);
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "amount_desc":
          return b.totalLoanAmount - a.totalLoanAmount;
        case "amount_asc":
          return a.totalLoanAmount - b.totalLoanAmount;
        case "latest":
        default:
          return b.updatedAt.localeCompare(a.updatedAt) || bPeriod.localeCompare(aPeriod);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterStatus !== "all" ? 1 : 0) + (hasPeriodFilter ? 1 : 0) + (filterEmployeeId ? 1 : 0);
  const resetFilters = () => {
    setFilterStatus("all");
    setFilterPeriodYear(null);
    setFilterPeriodMonth(null);
    setPickerYear(new Date().getFullYear());
    setFilterEmployeeId("");
  };

  const paginationKey = `${query}|${filterStatus}|${filterPeriodYear}|${filterPeriodMonth}|${filterEmployeeId}|${pageSize}|${sortBy}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  useEffect(() => {
    setViewTarget((prev) => {
      if (!prev) return null;
      return groups.find((g) => g.key === prev.key) ?? null;
    });
  }, [groups]);

  const loadViewSchedules = useCallback(async (group: LoanEmployeeGroup) => {
    if (!canViewInstallments || group.loans.length === 0) {
      setViewSchedules({});
      setViewSchedulesLoading(false);
      return;
    }
    setViewSchedulesLoading(true);
    try {
      const entries = await Promise.all(
        group.loans.map(async (loan) => {
          try {
            const res = await listLoanInstallments({
              employeeId: loan.employeeId,
              loanId: loan.id,
              perPage: 100,
              page: 1,
            });
            return [loan.id, buildInstallmentSchedule(loan, res.rows)] as const;
          } catch {
            return [loan.id, []] as const;
          }
        }),
      );
      setViewSchedules(Object.fromEntries(entries));
    } finally {
      setViewSchedulesLoading(false);
    }
  }, [canViewInstallments]);

  const openGroupView = useCallback(
    (group: LoanEmployeeGroup) => {
      setViewTarget(group);
      setViewSchedules({});
      void loadViewSchedules(group);
    },
    [loadViewSchedules],
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (r: EmployeeLoanRecord) => {
    if (r.hasPaidInstallment) {
      toast.error("This loan cannot be edited because an installment has already been paid.");
      return;
    }
    setEditing(r);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingTarget || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteEmployeeLoan({
        employeeId: deletingTarget.employeeId,
        id: deletingTarget.id,
      });
      toast.success(result.message);
      const deletedId = deletingTarget.id;
      setDeletingTarget(null);
      setPage(1);
      await load();
      if (viewTarget) {
        const remainingLoans = viewTarget.loans.filter((l) => l.id !== deletedId);
        if (remainingLoans.length === 0) {
          setViewTarget(null);
          setViewSchedules({});
        } else {
          const nextGroup = {
            ...viewTarget,
            loans: remainingLoans,
            totalLoanAmount: remainingLoans.reduce((sum, l) => sum + (parseLoanMoney(l.loanAmount) ?? 0), 0),
            totalRemaining: remainingLoans.reduce((sum, l) => sum + (parseLoanMoney(l.remainingAmount) ?? 0), 0),
            activeCount: remainingLoans.filter((l) => l.status === "active").length,
            completedCount: remainingLoans.filter((l) => l.status === "completed").length,
          };
          setViewTarget(nextGroup);
          setViewSchedules((prev) => {
            const copy = { ...prev };
            delete copy[deletedId];
            return copy;
          });
        }
      }
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const openSkip = (loan: EmployeeLoanRecord) => {
    setSkipLoan(loan);
    setSkipMonth(format(new Date(), "yyyy-MM"));
    setSkipPickerYear(new Date().getFullYear());
    setSkipNote("");
    setSkipOpen(true);
  };

  const openRepay = () => {
    setRepayAmount("");
    setRepayDate(format(new Date(), "yyyy-MM-dd"));
    setRepayNote("");
    setRepayPayment(emptyLoanPaymentDraft());
    setRepayOpen(true);
  };

  const submitRepay = async () => {
    if (repaying || !repayPlan || repayPlan.allocations.length === 0) return;
    const amount = repayAmountNumber;
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid repayment amount.");
      return;
    }
    if (repayPlan.excess >= 0.005) {
      toast.error(`Amount cannot exceed total remaining ${formatCurrency(repayPlan.totalRemaining)}.`);
      return;
    }
    if (!repayDate.trim()) {
      toast.error("Payment date is required.");
      return;
    }
    const paymentError = validateLoanPayment({
      paymentMethod: (repayPayment.paymentMethod || "") as ExpensePaymentMethod | "",
      bankId: repayPayment.bankId,
      referenceNo: repayPayment.referenceNo,
      chequeNo: repayPayment.chequeNo,
    });
    if (paymentError) {
      toast.error(paymentError);
      return;
    }

    setRepaying(true);
    try {
      const updatedById = new Map<string, EmployeeLoanRecord>();
      let lastMessage = "Repayment recorded.";
      for (const allocation of repayPlan.allocations) {
        const result = await recordLoanRepayment({
          employeeId: allocation.loan.employeeId,
          loanId: allocation.loan.id,
          amount: allocation.amount,
          paymentDate: repayDate,
          paymentMethod: (repayPayment.paymentMethod || "") as ExpensePaymentMethod | "",
          bankId: repayPayment.bankId,
          referenceNo: repayPayment.referenceNo,
          chequeNo: repayPayment.chequeNo,
          chequeDate: repayPayment.chequeDate,
          note: repayNote,
        });
        updatedById.set(result.record.id, result.record);
        lastMessage = result.message;
      }
      toast.success(
        repayPlan.allocations.length > 1
          ? `Repayment applied across ${repayPlan.allocations.length} loans.`
          : lastMessage,
      );
      setRepayOpen(false);
      setPage(1);
      await load();
      if (viewTarget) {
        setViewTarget((prev) => {
          if (!prev) return prev;
          const loans = prev.loans.map((l) => updatedById.get(l.id) ?? l);
          return {
            ...prev,
            loans,
            totalLoanAmount: loans.reduce((sum, l) => sum + (parseLoanMoney(l.loanAmount) ?? 0), 0),
            totalRemaining: loans.reduce((sum, l) => sum + (parseLoanMoney(l.remainingAmount) ?? 0), 0),
          };
        });
        const latestGroup = {
          ...viewTarget,
          loans: viewTarget.loans.map((l) => updatedById.get(l.id) ?? l),
        };
        await loadViewSchedules(latestGroup);
      }
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setRepaying(false);
    }
  };

  const submitSkip = async () => {
    if (!skipLoan || skipping) return;
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) {
      toast.error("Pick a month (YYYY-MM).");
      return;
    }
    const [yy, mm] = m.split("-").map((x) => Number(x));
    if (!yy || !mm) {
      toast.error("Pick a valid month.");
      return;
    }
    setSkipping(true);
    try {
      const result = await skipLoanInstallment({
        employeeId: skipLoan.employeeId,
        loanId: skipLoan.id,
        periodYear: yy,
        periodMonth: mm,
        note: skipNote,
      });
      toast.success(result.message);
      setSkipOpen(false);
      if (viewTarget) {
        const latest = groups.find((g) => g.key === viewTarget.key) ?? viewTarget;
        await loadViewSchedules(latest);
      }
      await load();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSkipping(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Employee loans access required"
      message="You don't have permission to view employee loans. Ask an administrator to grant employee_loan_can_view_any."
    />
  ) : (
    <Layout title="Employee loans">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Employee loans</h1>
            <p className="text-sm text-muted-foreground mt-1">Create loans and auto-deduct installments during salary processing.</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add loan
            </Button>
          ) : null}
        </div>

        <EmployeeLoanCreateDialog
          open={modalOpen}
          onOpenChange={(o) => {
            setModalOpen(o);
            if (!o) setEditing(null);
          }}
          loan={editing}
          onSaved={() => {
            setPage(1);
            void load();
          }}
        />

        <Dialog
          open={viewTarget != null}
          onOpenChange={(open) => {
            if (!open) {
              setViewTarget(null);
              setViewSchedules({});
            }
          }}
        >
          <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50 pr-8">
              <div className="flex items-start gap-2 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600 shrink-0">
                  <Banknote className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {viewTarget?.employeeName || "Loan details"}
                  </DialogTitle>
                  <div className="mt-0.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <p className="text-xs text-muted-foreground min-w-0">
                      {viewTarget
                        ? `${viewTarget.loans.length} loan${viewTarget.loans.length === 1 ? "" : "s"} · Rem ${formatCurrency(viewTarget.totalRemaining)}`
                        : "Employee loan details"}
                    </p>
                    {canClose && repayActiveLoans.length > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 gap-1 text-[11px] shrink-0 border-border/60 hover:border-emerald-500/50 hover:bg-emerald-500/10 hover:text-emerald-700"
                        onClick={() => openRepay()}
                      >
                        <HandCoins className="h-3.5 w-3.5" />
                        Loan Return
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="py-1">
              {!viewTarget || viewTarget.loans.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No loans for this employee.</div>
              ) : viewSchedulesLoading ? (
                <div className="py-12 text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading schedule…
                </div>
              ) : (
                <div className="space-y-3">
                  {(viewTarget.periodYear == null || viewTarget.periodMonth == null
                    ? loansByStartMonth(viewTarget.loans)
                    : [
                        {
                          key: periodKey(viewTarget.periodYear, viewTarget.periodMonth),
                          label: formatPeriodLabel(viewTarget.periodYear, viewTarget.periodMonth),
                          loans: viewTarget.loans,
                          totalLoanAmount: viewTarget.totalLoanAmount,
                          totalRemaining: viewTarget.totalRemaining,
                        },
                      ]
                  ).map((section) => (
                    <div key={section.key} className="space-y-2">
                      {section.loans.map((loan) => {
                        const schedule = viewSchedules[loan.id] ?? [];
                        const start = loanStartPeriod(loan);
                        const periodLabel = start
                          ? formatPeriodLabel(start.year, start.month)
                          : section.label;
                        const anyInstallmentPaid =
                          loan.hasPaidInstallment || schedule.some((row) => row.status === "paid");
                        const actionBtnClass =
                          "h-7 w-7 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors";
                        return (
                          <div key={loan.id} className="space-y-1.5">
                            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 text-sm text-foreground">
                                <span className="font-semibold whitespace-nowrap">{periodLabel}</span>
                                <LoanStatusPill status={loan.status} />
                                <span className="font-bold tabular-nums text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                                  {formatCurrency(parseLoanMoney(loan.loanAmount))}
                                </span>
                                <span className="tabular-nums whitespace-nowrap">
                                  Rem {formatCurrency(parseLoanMoney(loan.remainingAmount))}
                                </span>
                                <span className="text-border" aria-hidden>
                                  ·
                                </span>
                                <span className="tabular-nums whitespace-nowrap">
                                  Inst {formatCurrency(parseLoanMoney(loan.installmentAmount))}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {canUpdate && !anyInstallmentPaid && loan.status === "active" ? (
                                  <Tooltip delayDuration={200}>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className={actionBtnClass}
                                        onClick={() => openEdit(loan)}
                                        aria-label="Edit loan"
                                      >
                                        <Edit className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Edit loan</TooltipContent>
                                  </Tooltip>
                                ) : null}
                                {canSkipInstallment ? (
                                  <Tooltip delayDuration={200}>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2 text-[11px] border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                        disabled={loan.status !== "active"}
                                        onClick={() => openSkip(loan)}
                                        aria-label="Skip installment"
                                      >
                                        Skip
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Skip installment</TooltipContent>
                                  </Tooltip>
                                ) : null}
                                {canClose && !anyInstallmentPaid && loan.status === "active" ? (
                                  <Tooltip delayDuration={200}>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2 text-[11px] border-destructive/40 text-destructive hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                        onClick={() => setDeletingTarget(loan)}
                                        aria-label="Delete loan"
                                      >
                                        Delete
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Delete loan</TooltipContent>
                                  </Tooltip>
                                ) : null}
                              </div>
                            </div>
                            {canViewInstallments ? (
                              schedule.length > 0 ? (
                                <LoanInstallmentsPanel rows={schedule} />
                              ) : (
                                <div className="rounded-xl border border-border/60 px-3 py-4 text-center text-sm text-muted-foreground">
                                  No installments found for this loan.
                                </div>
                              )
                            ) : (
                              <div className="rounded-xl border border-border/60 px-3 py-4 text-center text-sm text-muted-foreground">
                                You don’t have permission to view installment schedules.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {viewTarget.loans.length > 1 ? (
                    <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                      <span className="text-sm font-semibold text-foreground">Grand total</span>
                      <div className="text-right text-sm tabular-nums">
                        <span className="font-bold text-emerald-700 dark:text-emerald-400">
                          {formatCurrency(viewTarget.totalLoanAmount)}
                        </span>
                        <span className="mx-1.5 text-border">·</span>
                        <span>
                          Rem {formatCurrency(viewTarget.totalRemaining)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setViewTarget(null);
                  setViewSchedules({});
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Skip Installment Dialog ── */}
        <Dialog open={skipOpen} onOpenChange={setSkipOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Skip installment for a month</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-1">
              {skipLoan ? (
                <div className="text-xs text-muted-foreground">
                  Loan <span className="text-foreground font-semibold">{skipLoan.loanAmount}</span> · Remaining{" "}
                  <span className="text-foreground font-semibold">{skipLoan.remainingAmount}</span>
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Popover modal={true} open={skipPickerOpen} onOpenChange={setSkipPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className={cn("glass-input h-10 w-full justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-70 shrink-0" />
                      {skipMonthLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-4 z-[100]" align="start" sideOffset={6}>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y - 1)}
                        aria-label="Previous year"
                      >
                        ‹
                      </Button>
                      <div className="flex-1">
                        <Select value={String(skipPickerYear)} onValueChange={(v) => setSkipPickerYear(Number(v))}>
                          <SelectTrigger className="glass-input h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 7 }).map((_, i) => {
                              const y = new Date().getFullYear() - 3 + i;
                              return (
                                <SelectItem key={y} value={String(y)}>
                                  {y}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y + 1)}
                        aria-label="Next year"
                      >
                        ›
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {monthOptions.map((m) => {
                        const iso = `${skipPickerYear}-${String(m.v).padStart(2, "0")}`;
                        const active = iso === skipMonth;
                        return (
                          <Button
                            key={m.v}
                            type="button"
                            variant={active ? "premium" : "outline"}
                            className={cn("h-9", !active && "glass-input")}
                            onClick={() => {
                              setSkipMonth(iso);
                              setSkipPickerOpen(false);
                            }}
                          >
                            {m.label}
                          </Button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Input className="glass-input h-10" value={skipNote} onChange={(e) => setSkipNote(e.target.value)} placeholder="e.g. Employee requested skip" />
              </div>
              <div className="text-xs text-muted-foreground">
                Salary processing for that month will <strong className="text-foreground">not deduct</strong> this loan installment.
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setSkipOpen(false)} disabled={skipping}>
                Cancel
              </Button>
              <Button variant="premium" className="gap-2" onClick={() => void submitSkip()} disabled={skipping}>
                {skipping ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Skip month
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Record outside repayment ── */}
        <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
          <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <HandCoins className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">Record Loan Return</DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    Enter the amount received — Schedule Impact shows which loan(s) and months will adjust (oldest loan first).
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-foreground">
                      Amount received <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      className="glass-input h-10"
                      value={repayAmount}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "");
                        if (!raw) {
                          setRepayAmount("");
                          return;
                        }
                        const [whole, ...rest] = raw.split(".");
                        const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
                        setRepayAmount(rest.length ? `${formatted}.${rest.join("")}` : formatted);
                      }}
                      placeholder="e.g. 12,000"
                      inputMode="decimal"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Max across active loans {formatCurrency(repayTotalRemaining)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-foreground">
                      Payment date <span className="text-destructive">*</span>
                    </Label>
                    <DatePartsPicker
                      value={repayDate}
                      onChange={setRepayDate}
                      fromYear={2000}
                      toYear={new Date().getFullYear() + 1}
                      placeholder="Select date"
                    />
                  </div>
                </div>

                <LoanPaymentMethodFields
                  value={repayPayment}
                  onChange={(patch) => setRepayPayment((prev) => ({ ...prev, ...patch }))}
                  banks={banks}
                  stacked
                  label="Received Via"
                />

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Note (optional)</Label>
                  <Input
                    className="glass-input h-10"
                    value={repayNote}
                    onChange={(e) => setRepayNote(e.target.value)}
                    placeholder="e.g. Cash received at office"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2 pb-2">
                    <Calculator className="w-4 h-4 text-primary" />
                    <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Schedule Impact</h4>
                  </div>

                  <div className="space-y-3 pt-3 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total remaining</span>
                      <span className="font-semibold tabular-nums text-foreground">
                        {formatCurrency(repayTotalRemaining)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">This return</span>
                      <span className="font-bold text-emerald-700 dark:text-emerald-400 tabular-nums">
                        {repayAmountNumber > 0 ? formatCurrency(repayAmountNumber) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                      <span className="text-muted-foreground">Remaining after</span>
                      <span className="font-extrabold text-primary text-sm tabular-nums">
                        {repayImpact
                          ? formatCurrency(
                              Math.max(0, Math.round((repayImpact.totalRemaining - repayImpact.applied) * 100) / 100),
                            )
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Loans adjusted</span>
                      <span className="font-bold text-foreground">
                        {repayImpact ? repayImpact.allocations.length : "—"}
                      </span>
                    </div>

                    {repayImpact ? (
                      <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-3 mt-2 max-h-[18rem] overflow-y-auto">
                        {repayImpact.allocations.map((allocation) => {
                          const start = loanStartPeriod(allocation.loan);
                          const loanTitle = start
                            ? formatPeriodLabel(start.year, start.month)
                            : `Loan #${allocation.loan.id}`;
                          return (
                            <div key={allocation.loan.id} className="space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-bold uppercase tracking-wide text-foreground">
                                    {loanTitle}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground truncate">
                                    {loanOptionLabel(allocation.loan)}
                                  </p>
                                </div>
                                <span className="shrink-0 font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                                  {formatCurrency(allocation.amount)}
                                </span>
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                Posts against{" "}
                                <span className="font-semibold text-foreground">
                                  {allocation.impact.repaymentPeriodLabel}
                                </span>
                                {" · "}
                                Rem after{" "}
                                <span className="font-semibold text-foreground">
                                  {formatCurrency(allocation.impact.remainingAfter)}
                                </span>
                              </p>
                              {allocation.impact.coveredPendingLabels.length > 0 ? (
                                <p className="text-[10px] text-muted-foreground">
                                  Months recalculated:{" "}
                                  <span className="font-semibold text-foreground">
                                    {allocation.impact.coveredPendingLabels.join(", ")}
                                  </span>
                                </p>
                              ) : null}
                              {allocation.impact.rows.length > 0 ? (
                                <div className="rounded-md border border-border/50 overflow-hidden">
                                  <table className="w-full text-xs">
                                    <thead className="bg-muted/80">
                                      <tr className="border-b border-border/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                                        <th className="text-left font-semibold py-1.5 px-2">Period</th>
                                        <th className="text-left font-semibold py-1.5 px-2">Status</th>
                                        <th className="text-right font-semibold py-1.5 px-2">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {allocation.impact.rows.map((row) => (
                                        <tr
                                          key={`${allocation.loan.id}-${row.key}`}
                                          className="border-b border-border/40 last:border-0"
                                        >
                                          <td className="py-1.5 px-2 font-medium text-foreground whitespace-nowrap">
                                            {row.periodLabel}
                                          </td>
                                          <td className="py-1.5 px-2 text-muted-foreground">Pending</td>
                                          <td className="py-1.5 px-2 text-right font-semibold tabular-nums text-foreground">
                                            {formatCurrency(row.amount)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-[10px] text-muted-foreground italic">
                                  No pending installments remaining after this return.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                        {repayActiveLoans.length === 0
                          ? "No active loans with remaining balance."
                          : repayAmountNumber <= 0
                            ? "Enter return amount to see which loan(s) will be adjusted."
                            : repayPlan && repayPlan.excess >= 0.005
                              ? `Amount exceeds total remaining ${formatCurrency(repayPlan.totalRemaining)}.`
                              : "Enter a valid amount to preview month adjustments."}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
                  <Info className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span>
                    Amount applies to the oldest active loan first, then spills to the next loan if needed.
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={() => setRepayOpen(false)} disabled={repaying}>
                Cancel
              </Button>
              <Button
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                onClick={() => void submitRepay()}
                disabled={repaying || !repayImpact || repayAmountNumber <= 0}
              >
                {repaying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <HandCoins className="w-4 h-4" />
                    Record loan return
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Search + Sheet Filters ── */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employee, amount, status…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet
              open={filtersOpen}
              onOpenChange={(open) => {
                setFiltersOpen(open);
                if (open && filterPeriodYear != null) {
                  setPickerYear(filterPeriodYear);
                }
              }}
            >
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter loans</SheetTitle>
                  <SheetDescription>Refine by employee, status, and salary period.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Employee</Label>
                    <EmployeeSelect
                      value={filterEmployeeId}
                      onChange={setFilterEmployeeId}
                      employees={employees}
                      clearable
                      clearLabel="All employees"
                      triggerClassName="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as never)}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <SalaryPeriodFilterField
                    selectedYear={filterPeriodYear}
                    selectedMonth={filterPeriodMonth}
                    onChange={(year, month) => {
                      setFilterPeriodYear(year);
                      setFilterPeriodMonth(month);
                    }}
                    browseYear={pickerYear}
                    onBrowseYearChange={setPickerYear}
                  />
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          {/* ── Controls bar: per-page, sort, grid/list ── */}
          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="loan-page-size-desktop" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as LoanSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Amount: High to Low</SelectItem>
                    <SelectItem value="amount_asc">Amount: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* mobile controls */}
          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="loan-page-size-mobile" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-2">
              <div className="w-[170px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as LoanSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Amount: High to Low</SelectItem>
                    <SelectItem value="amount_asc">Amount: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading loans…
          </div>
        ) : null}

        {!loading && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0 ? 'No loans yet. Click "Add loan" to create one.' : "No loans match your search."}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {paginatedItems.map((group, i) => {
              const name = group.employeeName || "—";
              const photo = employeePhoto(employees, group.employeeId);
              const period = loanGroupPeriodSummary(group);
              const statusLabel =
                group.activeCount > 0 && group.completedCount > 0
                  ? `${group.activeCount} active · ${group.completedCount} completed`
                  : group.activeCount > 0
                    ? "Active"
                    : "Completed";
              return (
                <motion.div key={group.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                  <CatalogEntityCard
                    className="h-full flex flex-col"
                    wrapText
                    title={name}
                    avatarUrl={photo}
                    titleIcon={User}
                    statusBadge={
                      group.activeCount > 0
                        ? { label: statusLabel, className: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" }
                        : { label: statusLabel, className: "bg-muted text-muted-foreground" }
                    }
                    primaryMeta={{
                      label: "Period",
                      value: `${period} · ${group.loans.length} loan${group.loans.length === 1 ? "" : "s"}`,
                      icon: Banknote,
                      iconClassName: "text-emerald-600",
                    }}
                    secondaryMeta={{
                      label: "Remaining",
                      value: formatCurrency(group.totalRemaining),
                      icon: Banknote,
                      iconClassName: "text-sky-600",
                    }}
                    metaColumns={1}
                    footerRight={formatCurrency(group.totalLoanAmount)}
                    footerRightClassName="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400"
                    onView={() => openGroupView(group)}
                    viewIconOnly
                  />
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-2">
            {paginatedItems.map((group, i) => {
              const name = group.employeeName || "—";
              const photo = employeePhoto(employees, group.employeeId);
              const period = loanGroupPeriodSummary(group);
              const statusBit =
                group.activeCount > 0 && group.completedCount > 0
                  ? `${group.activeCount} active · ${group.completedCount} completed`
                  : group.activeCount > 0
                    ? "Active"
                    : "Completed";
              const metaLine = [
                period,
                `${group.loans.length} loan${group.loans.length === 1 ? "" : "s"}`,
                statusBit,
                `Rem ${formatCurrency(group.totalRemaining)}`,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <motion.div
                  key={group.key}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.015 }}
                  className="glass-card px-3 py-2.5"
                >
                  <div className="flex items-center gap-2.5">
                    <LoanEmployeeAvatar name={name} photoUrl={photo} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center justify-between gap-3 min-w-0 flex-1">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-foreground truncate min-w-0">
                              {name}
                            </h3>
                            <p className="mt-0.5 truncate text-xs text-muted-foreground" title={metaLine}>
                              {metaLine}
                            </p>
                          </div>
                          <span className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400 shrink-0 text-right">
                            {formatCurrency(group.totalLoanAmount)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                onClick={() => openGroupView(group)}
                                aria-label="View loan details"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">View details</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && sorted.length > 0 ? (
          <GridPaginationFooter totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} page={page} totalPages={totalPages} onPageChange={setPage} />
        ) : null}

        <ConfirmAlertDialog
          open={!!deletingTarget}
          onOpenChange={(o) => !o && setDeletingTarget(null)}
          title="Delete this loan?"
          description={
            deletingTarget ? (
              <>
                Permanently delete loan{" "}
                <strong className="text-foreground font-semibold">
                  {formatCurrency(parseLoanMoney(deletingTarget.loanAmount))}
                </strong>{" "}
                with remaining{" "}
                <strong className="text-foreground font-semibold">
                  {formatCurrency(parseLoanMoney(deletingTarget.remainingAmount))}
                </strong>
                ? This removes loan records and related journal entries.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}
